import React, { useState, useEffect } from 'react';
import { profileApi } from '../api/profile';
import './Profile.scss';

const Profile = () => {
  const [isEditing, setIsEditing] = useState({
    personalInfo: false,
    currentIllnesses: false,
    medicalHistory: false,
    familyHistory: false,
    medications: false,
    allergies: false,
    lifestyleHabits: false
  });

  const [userProfile, setUserProfile] = useState({
    personalInfo: {
      name: "",
      email: "",
      dateOfBirth: "",
      gender: "",
    },
    medicalInfo: {
      currentIllnesses: [],
      previousSurgeries: [],
      hospitalizations: [],
      familyHistory: [],
      pastDiagnoses: [],
      medications: [],
      allergies: [],
    },
    lifestyleHabits: {
      smoking: "",
      alcohol: "",
      exercise: "",
      diet: "",
    }
  });

  const [newItems, setNewItems] = useState({
    currentIllnesses: "",
    previousSurgeries: "",
    hospitalizations: "",
    pastDiagnoses: "",
    familyHistory: "",
    medications: "",
    allergies: ""
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setIsLoading(true);
    const { data, error } = await profileApi.getProfile();
    setIsLoading(false);
    
    if (error) {
      setError(error);
      return;
    }

    setUserProfile(data);
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    
    const today = new Date();
    const birth = new Date(birthDate);
    
    // Check if the date is valid
    if (isNaN(birth.getTime())) return '';
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const handleEdit = (section) => {
    setIsEditing(prev => ({ ...prev, [section]: true }));
    setError("");
  };

  const handleSave = async (section) => {
    try {
      let response;
      
      switch (section) {
        case 'personalInfo':
          response = await profileApi.updatePersonalInfo(userProfile.personalInfo);
          break;
        case 'lifestyleHabits':
          response = await profileApi.updateLifestyle(userProfile.lifestyleHabits);
          break;
        default:
          response = await profileApi.bulkUpdateProfile(userProfile);
      }

      if (response.error) {
        setError(response.error);
        return;
      }

      setUserProfile(response.data);
      setIsEditing(prev => ({ ...prev, [section]: false }));
    } catch (err) {
      setError('Failed to save changes. Please try again.');
    }
  };

  const handleCancel = (section) => {
    setIsEditing(prev => ({ ...prev, [section]: false }));
    fetchProfile(); // Reset to last saved state
    setError("");
  };

  const handlePersonalInfoChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value
      }
    }));
  };

  const handleAddItem = async (section, item) => {
    if (!item.trim()) return;
    
    const { error } = await profileApi.updateMedicalList(
      section.replace(/([A-Z])/g, '-$1').toLowerCase(),
      'add',
      item
    );

    if (error) {
      setError(error);
      return;
    }

    setUserProfile(prev => ({
      ...prev,
      medicalInfo: {
        ...prev.medicalInfo,
        [section]: [...prev.medicalInfo[section], item]
      }
    }));
    
    // Clear only the specific section's input
    setNewItems(prev => ({
      ...prev,
      [section]: ""
    }));
  };

  const handleRemoveItem = async (section, index) => {
    const { error } = await profileApi.updateMedicalList(
      section.replace(/([A-Z])/g, '-$1').toLowerCase(),
      'remove',
      null,
      index
    );

    if (error) {
      setError(error);
      return;
    }

    setUserProfile(prev => ({
      ...prev,
      medicalInfo: {
        ...prev.medicalInfo,
        [section]: prev.medicalInfo[section].filter((_, i) => i !== index)
      }
    }));
  };

  const handleLifestyleChange = (field, value) => {
    setUserProfile(prev => ({
      ...prev,
      lifestyleHabits: {
        ...prev.lifestyleHabits,
        [field]: value
      }
    }));
  };

  if (isLoading) {
    return <div className="profile-loading">Loading profile...</div>;
  }

  return (
    <div className="profile-container">
      {error && <div className="profile-error">{error}</div>}
      <h1>Profile</h1>
      
      {/* Personal Information Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Personal Information</h2>
          {!isEditing.personalInfo ? (
            <button className="edit-button" onClick={() => handleEdit('personalInfo')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('personalInfo')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('personalInfo')}>Cancel</button>
            </div>
          )}
        </div>
        <div className="info-grid">
          {isEditing.personalInfo ? (
            <>
              <div className="info-item">
                <label>Name:</label>
                <input
                  type="text"
                  value={userProfile.personalInfo.name}
                  onChange={(e) => handlePersonalInfoChange('name', e.target.value)}
                />
              </div>
              <div className="info-item">
                <label>Email:</label>
                <input
                  type="email"
                  value={userProfile.personalInfo.email}
                  onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
                />
              </div>
              <div className="info-item">
                <label>Date of Birth:</label>
                <input
                  type="date"
                  value={userProfile.personalInfo.dateOfBirth}
                  onChange={(e) => handlePersonalInfoChange('dateOfBirth', e.target.value)}
                />
              </div>
              <div className="info-item">
                <label>Gender:</label>
                <select
                  value={userProfile.personalInfo.gender}
                  onChange={(e) => handlePersonalInfoChange('gender', e.target.value)}
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="info-item">
                <label>Name:</label>
                <span>{userProfile.personalInfo.name}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{userProfile.personalInfo.email}</span>
              </div>
              <div className="info-item">
                <label>Date of Birth:</label>
                <span>{userProfile.personalInfo.dateOfBirth} (Age: {calculateAge(userProfile.personalInfo.dateOfBirth)})</span>
              </div>
              <div className="info-item">
                <label>Gender:</label>
                <span>{userProfile.personalInfo.gender}</span>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Current Medical Conditions Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Current Medical Conditions</h2>
          {!isEditing.currentIllnesses ? (
            <button className="edit-button" onClick={() => handleEdit('currentIllnesses')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('currentIllnesses')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('currentIllnesses')}>Cancel</button>
            </div>
          )}
        </div>
        <div className="medical-list">
          {isEditing.currentIllnesses && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.currentIllnesses}
                onChange={(e) => setNewItems(prev => ({ ...prev, currentIllnesses: e.target.value }))}
                placeholder="Add new condition"
              />
              <button onClick={() => handleAddItem('currentIllnesses', newItems.currentIllnesses)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.currentIllnesses.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.currentIllnesses.map((illness, index) => (
                <li key={index}>
                  {illness}
                  {isEditing.currentIllnesses && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('currentIllnesses', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No current medical conditions recorded</p>
          )}
        </div>
      </section>

      {/* Medical History Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Medical History</h2>
          {!isEditing.medicalHistory ? (
            <button className="edit-button" onClick={() => handleEdit('medicalHistory')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('medicalHistory')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('medicalHistory')}>Cancel</button>
            </div>
          )}
        </div>
        
        <h3>Previous Surgeries</h3>
        <div className="medical-list">
          {isEditing.medicalHistory && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.previousSurgeries}
                onChange={(e) => setNewItems(prev => ({ ...prev, previousSurgeries: e.target.value }))}
                placeholder="Add surgery"
              />
              <button onClick={() => handleAddItem('previousSurgeries', newItems.previousSurgeries)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.previousSurgeries.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.previousSurgeries.map((surgery, index) => (
                <li key={index}>
                  {surgery}
                  {isEditing.medicalHistory && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('previousSurgeries', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No previous surgeries recorded</p>
          )}
        </div>

        <h3>Hospitalizations</h3>
        <div className="medical-list">
          {isEditing.medicalHistory && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.hospitalizations}
                onChange={(e) => setNewItems(prev => ({ ...prev, hospitalizations: e.target.value }))}
                placeholder="Add hospitalization"
              />
              <button onClick={() => handleAddItem('hospitalizations', newItems.hospitalizations)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.hospitalizations.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.hospitalizations.map((hospitalization, index) => (
                <li key={index}>
                  {hospitalization}
                  {isEditing.medicalHistory && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('hospitalizations', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No hospitalizations recorded</p>
          )}
        </div>

        <h3>Past Diagnoses</h3>
        <div className="medical-list">
          {isEditing.medicalHistory && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.pastDiagnoses}
                onChange={(e) => setNewItems(prev => ({ ...prev, pastDiagnoses: e.target.value }))}
                placeholder="Add diagnosis"
              />
              <button onClick={() => handleAddItem('pastDiagnoses', newItems.pastDiagnoses)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.pastDiagnoses.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.pastDiagnoses.map((diagnosis, index) => (
                <li key={index}>
                  {diagnosis}
                  {isEditing.medicalHistory && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('pastDiagnoses', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No past diagnoses recorded</p>
          )}
        </div>
      </section>

      {/* Family Medical History Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Family Medical History</h2>
          {!isEditing.familyHistory ? (
            <button className="edit-button" onClick={() => handleEdit('familyHistory')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('familyHistory')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('familyHistory')}>Cancel</button>
            </div>
          )}
        </div>
        <div className="medical-list">
          {isEditing.familyHistory && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.familyHistory}
                onChange={(e) => setNewItems(prev => ({ ...prev, familyHistory: e.target.value }))}
                placeholder="Add family medical history"
              />
              <button onClick={() => handleAddItem('familyHistory', newItems.familyHistory)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.familyHistory.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.familyHistory.map((history, index) => (
                <li key={index}>
                  {history}
                  {isEditing.familyHistory && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('familyHistory', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No family medical history recorded</p>
          )}
        </div>
      </section>

      {/* Medications Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Current Medications</h2>
          {!isEditing.medications ? (
            <button className="edit-button" onClick={() => handleEdit('medications')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('medications')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('medications')}>Cancel</button>
            </div>
          )}
        </div>
        
        <div className="medical-list">
          {isEditing.medications && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.medications}
                onChange={(e) => setNewItems(prev => ({ ...prev, medications: e.target.value }))}
                placeholder="Add medication"
              />
              <button onClick={() => handleAddItem('medications', newItems.medications)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.medications.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.medications.map((medication, index) => (
                <li key={index}>
                  {medication}
                  {isEditing.medications && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('medications', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No current medications recorded</p>
          )}
        </div>
      </section>

      {/* Allergies Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Allergies</h2>
          {!isEditing.allergies ? (
            <button className="edit-button" onClick={() => handleEdit('allergies')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('allergies')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('allergies')}>Cancel</button>
            </div>
          )}
        </div>

        <div className="medical-list">
          {isEditing.allergies && (
            <div className="add-item">
              <input
                type="text"
                value={newItems.allergies}
                onChange={(e) => setNewItems(prev => ({ ...prev, allergies: e.target.value }))}
                placeholder="Add allergy"
              />
              <button onClick={() => handleAddItem('allergies', newItems.allergies)}>Add</button>
            </div>
          )}
          {userProfile.medicalInfo.allergies.length > 0 ? (
            <ul>
              {userProfile.medicalInfo.allergies.map((allergy, index) => (
                <li key={index}>
                  {allergy}
                  {isEditing.allergies && (
                    <button 
                      className="remove-button"
                      onClick={() => handleRemoveItem('allergies', index)}
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>No allergies recorded</p>
          )}
        </div>
      </section>

      {/* Lifestyle Habits Section */}
      <section className="profile-section">
        <div className="section-header">
          <h2>Lifestyle Habits</h2>
          {!isEditing.lifestyleHabits ? (
            <button className="edit-button" onClick={() => handleEdit('lifestyleHabits')}>Edit</button>
          ) : (
            <div className="button-group">
              <button className="save-button" onClick={() => handleSave('lifestyleHabits')}>Save</button>
              <button className="cancel-button" onClick={() => handleCancel('lifestyleHabits')}>Cancel</button>
            </div>
          )}
        </div>
        <div className="info-grid">
          {isEditing.lifestyleHabits ? (
            <>
              <div className="info-item">
                <label>Smoking:</label>
                <select
                  value={userProfile.lifestyleHabits.smoking}
                  onChange={(e) => handleLifestyleChange('smoking', e.target.value)}
                >
                  <option value="Never">Never</option>
                  <option value="Former">Former</option>
                  <option value="Current">Current</option>
                </select>
              </div>
              <div className="info-item">
                <label>Alcohol:</label>
                <select
                  value={userProfile.lifestyleHabits.alcohol}
                  onChange={(e) => handleLifestyleChange('alcohol', e.target.value)}
                >
                  <option value="Never">Never</option>
                  <option value="Occasionally">Occasionally</option>
                  <option value="Regularly">Regularly</option>
                </select>
              </div>
              <div className="info-item">
                <label>Exercise:</label>
                <select
                  value={userProfile.lifestyleHabits.exercise}
                  onChange={(e) => handleLifestyleChange('exercise', e.target.value)}
                >
                  <option value="None">None</option>
                  <option value="Occasional">Occasional</option>
                  <option value="Regular">Regular</option>
                  <option value="Frequent">Frequent</option>
                </select>
              </div>
              <div className="info-item">
                <label>Diet:</label>
                <select
                  value={userProfile.lifestyleHabits.diet}
                  onChange={(e) => handleLifestyleChange('diet', e.target.value)}
                >
                  <option value="Balanced">Balanced</option>
                  <option value="Vegetarian">Vegetarian</option>
                  <option value="Vegan">Vegan</option>
                  <option value="Keto">Keto</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div className="info-item">
                <label>Smoking:</label>
                <span>{userProfile.lifestyleHabits.smoking}</span>
              </div>
              <div className="info-item">
                <label>Alcohol:</label>
                <span>{userProfile.lifestyleHabits.alcohol}</span>
              </div>
              <div className="info-item">
                <label>Exercise:</label>
                <span>{userProfile.lifestyleHabits.exercise}</span>
              </div>
              <div className="info-item">
                <label>Diet:</label>
                <span>{userProfile.lifestyleHabits.diet}</span>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Profile; 